import { useMemo, useState } from "react";
import { Link } from "react-router";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { AlertTriangle, ArrowUpRight, Inbox, Sparkles } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { makeTooltip } from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import {
  ENRICHMENT_ENTITIES,
  ENRICHMENT_PARK_LIMIT,
  type EnrichmentEntity,
  enrichmentContentTypeOf,
  enrichmentEntityHref,
  enrichmentEntityLabel,
  enrichmentProblemOf,
  parkAttemptLabel,
  readEnrichmentJobRef,
  readEnrichmentParkState,
  readEnrichmentResult,
} from "@/lib/tina/enrichment-jobs";
import { ConfidenceMeter } from "@/pages/tina/enrichment/components/confidence-meter";
import {
  ENRICHMENT_PAGE_SIZE,
  ENRICHMENT_TAB_STORAGE_KEY,
  type EnrichmentQueueTab,
  useEnrichmentQueue,
} from "@/pages/tina/enrichment/hooks/use-enrichment-queue";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import { curatorReviewProblemOf } from "@/pages/tina/items/components/panels/hitl-panel/curator-review-resolution";
import type { PipelineItem } from "@/types/tina";

dayjs.extend(relativeTime);

const ALL_ENTITIES = "__all_entities__";

const TAB_LABEL: Record<EnrichmentQueueTab, string> = {
  parked: "Needs review",
  gave_up: "Gave up",
  // The artist-field-locks curator-review park (2026-09-28) — its own tab
  // because it runs on a dedicated `enrichment_artist_curation` job, not on
  // one of the eleven entity flows the type filter above narrows.
  curator_review: "Curator review",
};

/**
 * `/tina/enrichment` — the enrichment HITL surface.
 *
 * **Why its own page and not a filter on `/tina/hitl`.** The shared queue is
 * deliberately scrape-only: `list_events` hides enrichment rows unless a
 * `content_type` names one, and `metric_hitl_backlog` excludes the enrichment
 * reasons from the dashboard card. Both are correct, so the parks need a
 * surface of their own or they are invisible.
 *
 * Two tabs, because the enrichment gate has two states a curator can act on and
 * only one of them is a HITL row — see {@link EnrichmentQueueTab}.
 */
export const EnrichmentList = () => {
  const [tab, setTab] = usePersistedState<EnrichmentQueueTab>(
    ENRICHMENT_TAB_STORAGE_KEY,
    "parked",
  );
  const [entity, setEntity] = usePersistedState<string | null>(
    "tina-enrichment:entity",
    null,
  );

  const selectedEntity = ENRICHMENT_ENTITIES.includes(
    entity as EnrichmentEntity,
  )
    ? (entity as EnrichmentEntity)
    : null;

  const queue = useEnrichmentQueue(tab, selectedEntity);

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Enrichment review</h1>
          <p className="text-sm text-muted-foreground">
            Proposals the enrichment confidence gate parked. The entity is
            already written back as <strong>NeedsReview</strong> — nothing is on
            its canonical fields until one of these is resolved.
          </p>
        </div>
        {/* Curator review has exactly one entity (artist) and its own content
            type outside this filter's eleven, so the picker has nothing to
            narrow there. */}
        {tab !== "curator_review" && (
          <Select
            value={selectedEntity ?? ALL_ENTITIES}
            onValueChange={(value) =>
              setEntity(value === ALL_ENTITIES ? null : value)
            }
          >
            <SelectTrigger className="h-9 w-[220px]">
              <SelectValue placeholder="All entity types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_ENTITIES}>All entity types</SelectItem>
              {ENRICHMENT_ENTITIES.map((value) => {
                const count =
                  queue.countsByContentType[enrichmentContentTypeOf(value)];
                return (
                  <SelectItem key={value} value={value}>
                    {enrichmentEntityLabel(value)}
                    {typeof count === "number" ? ` (${count})` : ""}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs
        value={tab}
        onValueChange={(value) => setTab(value as EnrichmentQueueTab)}
      >
        <TabsList>
          <TabsTrigger value="parked">
            {TAB_LABEL.parked}
            {tab === "parked" && !queue.isLoading ? ` (${queue.total})` : ""}
          </TabsTrigger>
          <TabsTrigger value="gave_up">
            {TAB_LABEL.gave_up}
            {tab === "gave_up" && !queue.isLoading ? ` (${queue.total})` : ""}
          </TabsTrigger>
          <TabsTrigger value="curator_review">
            {TAB_LABEL.curator_review}
            {tab === "curator_review" && !queue.isLoading
              ? ` (${queue.total})`
              : ""}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "gave_up" && (
        <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
          Jobs that hit the 3-park cap and closed for manual review. The last
          proposal was written back but nothing was promoted — the entity is
          still <strong>NeedsReview</strong>. Open one to re-enrich it.
        </p>
      )}

      {tab === "curator_review" && (
        <p className="rounded-md border border-indigo-500/40 bg-indigo-500/10 px-3 py-2 text-sm">
          An automatic enrichment write proposed a value for a field a curator
          already edited on this artist. Nothing was written — the curated value
          still stands until one of these is resolved.
        </p>
      )}

      {queue.isError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          Could not load the enrichment queue. Parked proposals may be waiting.
        </p>
      )}

      {queue.truncated && (
        <p className="text-xs text-muted-foreground">
          <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
          {tab === "parked" || tab === "curator_review" ? (
            <>
              More than {ENRICHMENT_PAGE_SIZE} jobs match; only the newest{" "}
              {ENRICHMENT_PAGE_SIZE} are listed.
            </>
          ) : (
            // The give-up flag is jsonb, so the server cannot filter on it: this
            // tab reads the newest page of manual-review jobs and keeps the
            // give-ups out of it. Past the page, give-ups exist that were never
            // looked at — which is a different sentence from "200 rows are
            // hidden", and the one a curator needs to hear.
            <>
              Only the newest {ENRICHMENT_PAGE_SIZE} manual-review jobs were
              scanned for give-ups; older ones were not.
            </>
          )}
          {/* Curator review has no entity filter to narrow with — see the picker above. */}
          {tab !== "curator_review" && <> Narrow with the entity filter.</>}
        </p>
      )}

      {queue.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : queue.rows.length === 0 ? (
        // Not below a failed request: "every enrichment cleared its gate" is a
        // claim about the queue, and a request that never landed cannot make it.
        queue.isError ? null : (
          <EmptyState tab={tab} truncated={queue.truncated} />
        )
      ) : tab === "curator_review" ? (
        <CuratorReviewTable rows={queue.rows} />
      ) : (
        <EnrichmentTable rows={queue.rows} tab={tab} />
      )}
    </div>
  );
};

/**
 * `truncated` is what keeps this from over-claiming.
 *
 * Both sentences below are assertions about the WHOLE queue, and only a complete
 * page can carry them. The `gave_up` one is the dangerous half: its rows survive
 * a client-side filter, so an incomplete page can empty entirely and still say
 * "No job has hit the park cap" — about jobs it never looked at.
 */
function EmptyState({
  tab,
  truncated,
}: {
  tab: EnrichmentQueueTab;
  truncated: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed py-16 text-sm text-muted-foreground">
      {tab === "parked" ? (
        <>
          <Sparkles className="h-5 w-5 text-emerald-500" />
          Nothing parked — every enrichment cleared its gate.
        </>
      ) : tab === "curator_review" ? (
        <>
          <Sparkles className="h-5 w-5 text-emerald-500" />
          Nothing parked — every curated field agrees with the latest write.
        </>
      ) : truncated ? (
        <>
          <Inbox className="h-5 w-5" />
          No give-up in the newest {ENRICHMENT_PAGE_SIZE} manual-review jobs.
        </>
      ) : (
        <>
          <Inbox className="h-5 w-5" />
          No job has hit the park cap.
        </>
      )}
    </div>
  );
}

function CuratorReviewTable({ rows }: { rows: PipelineItem[] }) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Artist</TableHead>
            <TableHead className="w-[10rem]">Held fields</TableHead>
            <TableHead className="w-[7rem]">Age</TableHead>
            <TableHead className="w-[6rem]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <CuratorReviewRow key={row.id} item={row} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CuratorReviewRow({ item }: { item: PipelineItem }) {
  const problem = curatorReviewProblemOf(item.hitl_context);
  const entityId = problem?.entity_id ?? null;
  const fieldCount = problem?.fields.length ?? 0;

  return (
    <TableRow>
      <TableCell>
        {entityId ? (
          <div className="flex flex-col">
            {problem?.artist_name && (
              <span className="text-sm">{problem.artist_name}</span>
            )}
            <Link
              to={`/artists/edit/${entityId}`}
              className="font-mono text-xs text-blue-500 hover:underline"
            >
              {entityId}
            </Link>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs">
        {fieldCount} {fieldCount === 1 ? "field" : "fields"}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {makeTooltip(
          <span>{dayjs(item.created_at).fromNow(true)}</span>,
          dayjs(item.created_at).format("YYYY-MM-DD HH:mm"),
        )}
      </TableCell>
      <TableCell>
        <Button asChild size="sm" variant="outline">
          <Link to={`/tina/items/show/${item.id}`}>
            Review
            <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
      </TableCell>
    </TableRow>
  );
}

function EnrichmentTable({
  rows,
  tab,
}: {
  rows: PipelineItem[];
  tab: EnrichmentQueueTab;
}) {
  const { members } = useMembers();
  const memberName = useMemo(() => {
    const byId = new Map(members.map((member) => [member.id, member]));
    return (uid: string | null) => {
      if (!uid) return null;
      const member = byId.get(uid);
      return member?.displayName || member?.email || uid;
    };
  }, [members]);

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[10rem]">Entity</TableHead>
            <TableHead>Target</TableHead>
            <TableHead className="w-[12rem]">Confidence</TableHead>
            <TableHead className="w-[9rem]">Attempt</TableHead>
            <TableHead className="w-[11rem]">Requested by</TableHead>
            <TableHead className="w-[7rem]">Age</TableHead>
            <TableHead className="w-[6rem]" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <EnrichmentRow
              key={row.id}
              item={row}
              tab={tab}
              memberName={memberName}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function EnrichmentRow({
  item,
  tab,
  memberName,
}: {
  item: PipelineItem;
  tab: EnrichmentQueueTab;
  memberName: (uid: string | null) => string | null;
}) {
  const problem = enrichmentProblemOf(item.hitl_context);
  const result = readEnrichmentResult(item);
  const jobRef = readEnrichmentJobRef(item);

  // Three sources for the same two facts, in descending trustworthiness: the
  // parked problem, the enrich stage's result block, then the job payload —
  // which is the only one present on a job that never reached the stage.
  const entityType =
    problem?.entity_type ?? result?.entity_type ?? jobRef?.entity_type ?? null;
  const entityId =
    problem?.entity_id ?? result?.entity_id ?? jobRef?.entity_id ?? null;
  const confidence = problem?.confidence ?? result?.confidence ?? null;
  // Terminal-first, through the one reader `/tina/items` also uses. Reading
  // `problem` first printed the count of the park *before* the one that closed
  // the job, so every row in the "Gave up" tab was one low.
  const parkCount = readEnrichmentParkState(item).count;

  const entityHref = enrichmentEntityHref(entityType, entityId);
  const requestedBy = memberName(jobRef?.requested_by ?? null);

  return (
    <TableRow>
      <TableCell>
        <Badge className="bg-indigo-500/15 text-indigo-700 dark:text-indigo-300">
          {enrichmentEntityLabel(entityType)}
        </Badge>
      </TableCell>
      <TableCell>
        {entityId ? (
          entityHref ? (
            <Link
              to={entityHref}
              className="font-mono text-xs text-blue-500 hover:underline"
            >
              {entityId}
            </Link>
          ) : (
            <span className="font-mono text-xs">{entityId}</span>
          )
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell>
        <ConfidenceMeter
          confidence={confidence}
          threshold={problem?.threshold ?? null}
        />
      </TableCell>
      <TableCell className="text-xs">
        {tab === "gave_up" ? (
          <span className="text-amber-700 dark:text-amber-400">
            Gave up after {parkCount ?? ENRICHMENT_PARK_LIMIT}
          </span>
        ) : (
          parkAttemptLabel(parkCount)
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {requestedBy ??
          (jobRef?.source ? (
            <span className="font-mono">{jobRef.source}</span>
          ) : (
            "—"
          ))}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {makeTooltip(
          <span>{dayjs(item.created_at).fromNow(true)}</span>,
          dayjs(item.created_at).format("YYYY-MM-DD HH:mm"),
        )}
      </TableCell>
      <TableCell>
        <Button asChild size="sm" variant="outline">
          <Link to={`/tina/items/show/${item.id}`}>
            Review
            <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
          </Link>
        </Button>
      </TableCell>
    </TableRow>
  );
}
